import AdminLayout from "@/components/layout-admin";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Plus, KeyRound, Pencil, Trash2, Copy, BatteryLow, BatteryMedium, BatteryFull, BatteryWarning, ShieldOff } from "lucide-react";
import type { SmartLock, AccessCode, Reservation } from "@shared/schema"; import { useApartments } from "@/hooks/use-resources";

const PROVIDERS = ["generic", "august", "yale", "schlage", "nuki", "ttlock"] as const;
const LOCK_STATUSES = ["active", "offline", "maintenance"] as const;

function statusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "active": return "default";
    case "offline": return "destructive";
    case "maintenance": return "secondary";
    case "expired": return "secondary";
    case "revoked": return "destructive";
    default: return "outline";
  }
}

function BatteryIndicator({ level }: { level: number | null }) {
  if (level === null || level === undefined) return <span className="text-xs text-muted-foreground">N/A</span>;
  const Icon = level > 75 ? BatteryFull : level > 40 ? BatteryMedium : level > 15 ? BatteryLow : BatteryWarning;
  const color = level > 40 ? "text-green-600 dark:text-green-400" : level > 15 ? "text-yellow-600 dark:text-yellow-400" : "text-destructive";
  return (
    <span className={`flex items-center gap-1 text-xs ${color}`} data-testid="battery-indicator">
      <Icon className="w-4 h-4" />
      {level}%
    </span>
  );
}

export default function AdminSmartLocks() {
  const { toast } = useToast();

  const { data: locks, isLoading: locksLoading } = useQuery<SmartLock[]>({ queryKey: ["/api/admin/smart-locks"] });
  const { data: codes, isLoading: codesLoading } = useQuery<AccessCode[]>({ queryKey: ["/api/admin/access-codes"] });
  const { data: apartments } = useApartments();
  const { data: reservations } = useQuery<Reservation[]>({ queryKey: ["/api/admin/reservations"] });

  const [createLockOpen, setCreateLockOpen] = useState(false);
  const [editLock, setEditLock] = useState<SmartLock | null>(null);
  const [createCodeOpen, setCreateCodeOpen] = useState(false);

  const [lockForm, setLockForm] = useState({ apartmentId: "", provider: "generic", deviceId: "", name: "" });
  const [editLockForm, setEditLockForm] = useState({ apartmentId: "", provider: "generic", deviceId: "", name: "", status: "active" });
  const [codeForm, setCodeForm] = useState({ smartLockId: "", reservationId: "", code: "", label: "", validFrom: "", validTo: "" });

  const createLockMutation = useMutation({
    mutationFn: (data: { apartmentId: number; provider: string; deviceId: string; name: string }) =>
      apiRequest("POST", "/api/admin/smart-locks", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/smart-locks"] });
      setCreateLockOpen(false);
      setLockForm({ apartmentId: "", provider: "generic", deviceId: "", name: "" });
      toast({ title: "Smart lock registered" });
    },
    onError: () => toast({ title: "Failed to register lock", variant: "destructive" }),
  });

  const updateLockMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Record<string, unknown> }) =>
      apiRequest("PUT", `/api/admin/smart-locks/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/smart-locks"] });
      setEditLock(null);
      toast({ title: "Smart lock updated" });
    },
    onError: () => toast({ title: "Failed to update lock", variant: "destructive" }),
  });

  const deleteLockMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/admin/smart-locks/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/smart-locks"] });
      toast({ title: "Smart lock deleted" });
    },
    onError: () => toast({ title: "Failed to delete lock", variant: "destructive" }),
  });

  const createCodeMutation = useMutation({
    mutationFn: (data: { smartLockId: number; reservationId?: number | null; code: string; label: string; validFrom: string; validTo: string }) =>
      apiRequest("POST", "/api/admin/access-codes", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/access-codes"] });
      setCreateCodeOpen(false);
      setCodeForm({ smartLockId: "", reservationId: "", code: "", label: "", validFrom: "", validTo: "" });
      toast({ title: "Access code generated" });
    },
    onError: () => toast({ title: "Failed to generate code", variant: "destructive" }),
  });

  const revokeCodeMutation = useMutation({
    mutationFn: (id: number) => apiRequest("PUT", `/api/admin/access-codes/${id}`, { status: "revoked" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/access-codes"] });
      toast({ title: "Access code revoked" });
    },
    onError: () => toast({ title: "Failed to revoke code", variant: "destructive" }),
  });

  const getApartmentName = (id: number) => apartments?.find(a => a.id === id)?.name || `Apt #${id}`;
  const getLockName = (id: number) => locks?.find(l => l.id === id)?.name || `Lock #${id}`;
  const getReservationGuest = (id: number | null) => {
    if (!id) return "—";
    return reservations?.find(r => r.id === id)?.guestName || `Res #${id}`;
  };

  const handleCreateLock = () => {
    if (!lockForm.apartmentId || !lockForm.deviceId || !lockForm.name) return;
    createLockMutation.mutate({
      apartmentId: parseInt(lockForm.apartmentId),
      provider: lockForm.provider,
      deviceId: lockForm.deviceId,
      name: lockForm.name,
    });
  };

  const handleEditLock = (lock: SmartLock) => {
    setEditLock(lock);
    setEditLockForm({
      apartmentId: String(lock.apartmentId),
      provider: lock.provider,
      deviceId: lock.deviceId,
      name: lock.name,
      status: lock.status,
    });
  };

  const handleUpdateLock = () => {
    if (!editLock) return;
    updateLockMutation.mutate({
      id: editLock.id,
      data: {
        apartmentId: parseInt(editLockForm.apartmentId),
        provider: editLockForm.provider,
        deviceId: editLockForm.deviceId,
        name: editLockForm.name,
        status: editLockForm.status,
      },
    });
  };

  const handleCreateCode = () => {
    if (!codeForm.smartLockId || !codeForm.code || !codeForm.validFrom || !codeForm.validTo) return;
    createCodeMutation.mutate({
      smartLockId: parseInt(codeForm.smartLockId),
      reservationId: codeForm.reservationId ? parseInt(codeForm.reservationId) : null,
      code: codeForm.code,
      label: codeForm.label,
      validFrom: codeForm.validFrom,
      validTo: codeForm.validTo,
    });
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast({ title: "Code copied to clipboard" });
  };

  const generateCode = () => {
    const code = Math.random().toString().slice(2, 8);
    setCodeForm(prev => ({ ...prev, code }));
  };

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">Smart Locks</h1>
          <p className="text-muted-foreground mt-2">Manage smart lock devices and access codes for your properties.</p>
        </div>
      </div>

      <Tabs defaultValue="devices" data-testid="tabs-smart-locks">
        <TabsList data-testid="tabs-list">
          <TabsTrigger value="devices" data-testid="tab-devices">Devices</TabsTrigger>
          <TabsTrigger value="codes" data-testid="tab-access-codes">Access Codes</TabsTrigger>
        </TabsList>

        <TabsContent value="devices" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={createLockOpen} onOpenChange={setCreateLockOpen}>
              <DialogTrigger asChild>
                <Button data-testid="button-add-lock">
                  <Plus className="w-4 h-4 mr-2" /> Register Lock
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Register New Smart Lock</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Apartment</label>
                    <Select value={lockForm.apartmentId} onValueChange={v => setLockForm({ ...lockForm, apartmentId: v })}>
                      <SelectTrigger data-testid="select-lock-apartment">
                        <SelectValue placeholder="Select apartment" />
                      </SelectTrigger>
                      <SelectContent>
                        {apartments?.map(a => (
                          <SelectItem key={a.id} value={String(a.id)}>{a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Provider</label>
                    <Select value={lockForm.provider} onValueChange={v => setLockForm({ ...lockForm, provider: v })}>
                      <SelectTrigger data-testid="select-lock-provider">
                        <SelectValue placeholder="Select provider" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROVIDERS.map(p => (
                          <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Device ID</label>
                    <Input
                      placeholder="e.g. LOCK-001-ABC"
                      value={lockForm.deviceId}
                      onChange={e => setLockForm({ ...lockForm, deviceId: e.target.value })}
                      data-testid="input-lock-device-id"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Name</label>
                    <Input
                      placeholder="e.g. Front Door Lock"
                      value={lockForm.name}
                      onChange={e => setLockForm({ ...lockForm, name: e.target.value })}
                      data-testid="input-lock-name"
                    />
                  </div>
                  <Button className="w-full" onClick={handleCreateLock} disabled={createLockMutation.isPending} data-testid="button-submit-lock">
                    {createLockMutation.isPending ? "Registering..." : "Register Lock"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {locksLoading && (
            <div className="text-center py-12 text-muted-foreground">Loading devices...</div>
          )}

          {!locksLoading && (!locks || locks.length === 0) && (
            <div className="text-center py-12" data-testid="text-empty-locks">
              <KeyRound className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No smart locks registered yet. Add your first device to get started.</p>
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {locks?.map(lock => (
              <Card key={lock.id} data-testid={`card-lock-${lock.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-primary/10 rounded-xl flex-shrink-0">
                      <KeyRound className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold font-display" data-testid={`text-lock-name-${lock.id}`}>{lock.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1" data-testid={`text-lock-apartment-${lock.id}`}>
                        {getApartmentName(lock.apartmentId)}
                      </p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge variant="outline" data-testid={`badge-lock-provider-${lock.id}`}>
                          {lock.provider}
                        </Badge>
                        <Badge variant={statusVariant(lock.status)} data-testid={`badge-lock-status-${lock.id}`}>
                          {lock.status}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
                        <BatteryIndicator level={lock.batteryLevel} />
                        <span className="text-xs text-muted-foreground font-mono" data-testid={`text-lock-device-id-${lock.id}`}>
                          {lock.deviceId}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 mt-4 pt-4 border-t justify-end">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleEditLock(lock)}
                      data-testid={`button-edit-lock-${lock.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          data-testid={`button-delete-lock-${lock.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Smart Lock</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{lock.name}"? This will also remove all associated access codes. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="button-cancel-delete-lock">Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteLockMutation.mutate(lock.id)}
                            data-testid="button-confirm-delete-lock"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="codes" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={createCodeOpen} onOpenChange={(open) => {
              setCreateCodeOpen(open);
              if (open) generateCode();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="button-add-code">
                  <Plus className="w-4 h-4 mr-2" /> Generate Code
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Generate Access Code</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Smart Lock</label>
                    <Select value={codeForm.smartLockId} onValueChange={v => setCodeForm({ ...codeForm, smartLockId: v })}>
                      <SelectTrigger data-testid="select-code-lock">
                        <SelectValue placeholder="Select lock" />
                      </SelectTrigger>
                      <SelectContent>
                        {locks?.map(l => (
                          <SelectItem key={l.id} value={String(l.id)}>{l.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Reservation (optional)</label>
                    <Select value={codeForm.reservationId} onValueChange={v => setCodeForm({ ...codeForm, reservationId: v })}>
                      <SelectTrigger data-testid="select-code-reservation">
                        <SelectValue placeholder="No reservation" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No reservation</SelectItem>
                        {reservations?.map(r => (
                          <SelectItem key={r.id} value={String(r.id)}>{r.guestName}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Code</label>
                    <div className="flex gap-2">
                      <Input
                        value={codeForm.code}
                        onChange={e => setCodeForm({ ...codeForm, code: e.target.value })}
                        data-testid="input-code-value"
                      />
                      <Button variant="outline" onClick={generateCode} data-testid="button-regenerate-code">
                        Regenerate
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Label</label>
                    <Input
                      placeholder="e.g. Weekend guest"
                      value={codeForm.label}
                      onChange={e => setCodeForm({ ...codeForm, label: e.target.value })}
                      data-testid="input-code-label"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Valid From</label>
                      <Input
                        type="datetime-local"
                        value={codeForm.validFrom}
                        onChange={e => setCodeForm({ ...codeForm, validFrom: e.target.value })}
                        data-testid="input-code-valid-from"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Valid To</label>
                      <Input
                        type="datetime-local"
                        value={codeForm.validTo}
                        onChange={e => setCodeForm({ ...codeForm, validTo: e.target.value })}
                        data-testid="input-code-valid-to"
                      />
                    </div>
                  </div>
                  <Button className="w-full" onClick={handleCreateCode} disabled={createCodeMutation.isPending} data-testid="button-submit-code">
                    {createCodeMutation.isPending ? "Generating..." : "Generate Code"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {codesLoading && (
            <div className="text-center py-12 text-muted-foreground">Loading access codes...</div>
          )}

          {!codesLoading && (!codes || codes.length === 0) && (
            <div className="text-center py-12" data-testid="text-empty-codes">
              <KeyRound className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No access codes yet. Generate your first code to get started.</p>
            </div>
          )}

          {codes && codes.length > 0 && (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Lock</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Label</TableHead>
                      <TableHead>Guest</TableHead>
                      <TableHead>Valid From</TableHead>
                      <TableHead>Valid To</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {codes.map(code => (
                      <TableRow key={code.id} data-testid={`row-code-${code.id}`}>
                        <TableCell data-testid={`text-code-lock-${code.id}`}>
                          {getLockName(code.smartLockId)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <span className="font-mono font-bold" data-testid={`text-code-value-${code.id}`}>{code.code}</span>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => copyCode(code.code)}
                              data-testid={`button-copy-code-${code.id}`}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell data-testid={`text-code-label-${code.id}`}>
                          {code.label || "—"}
                        </TableCell>
                        <TableCell data-testid={`text-code-guest-${code.id}`}>
                          {getReservationGuest(code.reservationId)}
                        </TableCell>
                        <TableCell data-testid={`text-code-valid-from-${code.id}`}>
                          {new Date(code.validFrom).toLocaleDateString("en-US", { timeZone: "UTC" })}
                        </TableCell>
                        <TableCell data-testid={`text-code-valid-to-${code.id}`}>
                          {new Date(code.validTo).toLocaleDateString("en-US", { timeZone: "UTC" })}
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusVariant(code.status)} data-testid={`badge-code-status-${code.id}`}>
                            {code.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {code.status === "active" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => revokeCodeMutation.mutate(code.id)}
                              disabled={revokeCodeMutation.isPending}
                              data-testid={`button-revoke-code-${code.id}`}
                            >
                              <ShieldOff className="w-4 h-4 mr-1" />
                              Revoke
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!editLock} onOpenChange={(open) => { if (!open) setEditLock(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Smart Lock</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Apartment</label>
              <Select value={editLockForm.apartmentId} onValueChange={v => setEditLockForm({ ...editLockForm, apartmentId: v })}>
                <SelectTrigger data-testid="select-edit-lock-apartment">
                  <SelectValue placeholder="Select apartment" />
                </SelectTrigger>
                <SelectContent>
                  {apartments?.map(a => (
                    <SelectItem key={a.id} value={String(a.id)}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Provider</label>
              <Select value={editLockForm.provider} onValueChange={v => setEditLockForm({ ...editLockForm, provider: v })}>
                <SelectTrigger data-testid="select-edit-lock-provider">
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  {PROVIDERS.map(p => (
                    <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Device ID</label>
              <Input
                value={editLockForm.deviceId}
                onChange={e => setEditLockForm({ ...editLockForm, deviceId: e.target.value })}
                data-testid="input-edit-lock-device-id"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Name</label>
              <Input
                value={editLockForm.name}
                onChange={e => setEditLockForm({ ...editLockForm, name: e.target.value })}
                data-testid="input-edit-lock-name"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select value={editLockForm.status} onValueChange={v => setEditLockForm({ ...editLockForm, status: v })}>
                <SelectTrigger data-testid="select-edit-lock-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {LOCK_STATUSES.map(s => (
                    <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full" onClick={handleUpdateLock} disabled={updateLockMutation.isPending} data-testid="button-update-lock">
              {updateLockMutation.isPending ? "Updating..." : "Update Lock"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}